import {
  TdsDropdown,
  TdsDropdownOption,
  TdsSpinner,
  TdsTooltip,
} from '@scania/tegel-react'
import styled from 'styled-components'
import {
  SvgInfo,
  SvgScaniaSuper,
  SvgTick,
} from '../../../../components/SvgImports'
import { CustomScrollbarMixin } from '../../../../css/CustomScrollbarMixin'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  openGvPanel,
  setGvSidePanelContent,
  setReadMoreNode,
} from '../../../../store/sidePanelSlice'
import { SidePanelContent } from '../../../../store/types'
import { getMenuState, setMenuTreeAllView } from '../../../../store/menuSlice'
import {
  getSessionInitDataState,
  getUserSelectionTimestamp,
} from '../../../../store/sessionDataSlice'
import { useClient } from '../../../../utils/useClient'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { GuidedViewMenuLeafNode } from '../../../../api/generated'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickBmReadMoreEvent,
  ScaniaAdobeTrackingMenuClickEvent,
  pushAdobeEvent,
} from '../../../../utils/adobeAnalytics'
import { BreakpointWidthPx } from '../../../../css/BreakpointWidthPx'
import { TextId, useTextsV2 } from '../../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../../css/BreakpointHeightPx'
import { Size2d } from '../../../../types/Size2d'
import { blinkKeyframesBorder } from '../../MenuBlinkAnimationBorder'
import { ViewAllOrLessOptionsButton } from '../../GuidedViewPage/ViewAllOrLessOptionsButtonMobile'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../../utils/getUrlCidParameter'
import { TestElementTypeId } from '../../../../types/TestAttributeId'
import { getLatestConfigChangeResult } from '../../../../store/truckInfoSlice'

const MenuContentRoot = styled.div`
  ${CustomScrollbarMixin}
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  padding-right: 24px;
  overflow-y: scroll;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding: 16px;
    overflow-y: unset;
  }
`

const Header = styled.div`
  margin-bottom: 8px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: bold;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 14px;
  }
`

const SelectedOptionWrapper = styled.div`
  display: flex;
  margin-top: 32px;
  margin-bottom: 16px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const SelectedOptionText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: bold;
`

const CardsWrapper = styled.div``

interface CardProps {
  $isSelected: boolean
  $isHighlighted: boolean
}

const OptionCard = styled.div<CardProps>`
  width: 100%;
  height: 120px;
  margin-bottom: 8px;
  border-radius: 4px;
  border: ${(props) => (props.$isSelected ? '2px' : '1px')} solid
    ${(props) =>
      props.$isSelected ? 'var(--tds-information)' : 'var(--tds-grey-300)'};
  display: flex;
  justify-content: center;
  padding: 16px;

  animation-duration: 500ms;
  animation-iteration-count: 3;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframesBorder : 'unset'};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    height: 60px;
    padding: 8px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 80px;
    padding: 12px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${({ $isSelected }) => !$isSelected && 'cursor: pointer;'}
    }
  }
`

const CardImage = styled.div`
  width: 130px;
  min-width: 130px;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;

  img {
    max-height: 100%;
    max-width: 100%;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 80px;
    min-width: 80px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 100px;
    min-width: 100px;
  }
`

const CardTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  overflow: hidden;
  padding-right: 8px;
`

const CardTextHeader = styled.div`
  height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 16px;
  margin-bottom: 4px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 14px;
    margin-bottom: 2px;
  }
`

const CardStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 32px;
  min-width: 32px;
  justify-content: center;

  svg {
    height: 24px;
  }
`

const ReadMoreAndSuperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CardSuper = styled.div`
  background-color: var(--tds-blue-500);
  border-radius: 16px;
  width: fit-content;
  padding: 1px 10px;

  svg {
    height: 9px;

    path {
      fill: white;
    }
  }
`

const CardReadMore = styled.div`
  display: flex;
  align-items: end;
  margin-top: 8px;
  gap: 4px;
  font-size: 10px;
  height: 16px;
  align-items: center;
  width: fit-content;
  text-decoration: underline;

  svg {
    height: 16px;
    width: 16px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: help;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin: 0px;
  }
`

const DropdownWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }
`

interface NonColourProps {
  parentSize: Size2d
  handleItemSelection: (id: string, text: string) => void
  highlightedId: string | null
  selectedMenuLevel2: string | null | undefined
  selectedMenuLevel3: string | null | undefined
}

export function MenuContentNonColourAllView({
  parentSize,
  handleItemSelection,
  highlightedId,
  selectedMenuLevel2,
  selectedMenuLevel3,
}: NonColourProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const client = useClient()
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  const scrollToHighlightedItemRef = useRef<HTMLDivElement | null>(null)
  const scrollToSelectedItemRef = useRef<HTMLDivElement | null>(null)
  const menuTree = useAppSelector(getMenuState).menuResultAllView?.menuTree
  const menuLevel2 = menuTree?.subMenus
    ? menuTree?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel3 = menuLevel2
    ? menuLevel2.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel4 = menuLevel3
    ? menuLevel3.find((menu) => menu.isOpen === true)?.leafItems
    : null
  const selectedLeafItem = menuLevel4?.find((item) => item.isSelected === true)
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')
  const selectedCabColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedCabColorNode
  const selectedChassiColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedChassiColorNode
  const superEngines = useAppSelector(getLatestConfigChangeResult)?.superEngines

  const handleReadmoreClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      node: GuidedViewMenuLeafNode,
    ) => {
      e.stopPropagation()
      e.preventDefault()

      dispatch(setReadMoreNode({ id: node.id, title: node.shortText || null }))
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.readMore))

      const trackingEvent: ScaniaAdobeTrackingClickBmReadMoreEvent = {
        event: ScaniaAdobeEventId.BmReadMoreClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [dispatch],
  )

  const handleSubMenuDropdownSelection = (
    evt: CustomEvent<{ name: string; value: string }>,
  ) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const selectedSubMenuId = evt.detail.value
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: selectedSubMenuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: selectedSubMenuId,
            optionClickedText: '',
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  const capitalizeFirstLetter = (text: string | null | undefined) => {
    if (!text) {
      return null
    }
    const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1)
    return capitalizedText
  }

  // Scroll down to highlighted item
  useLayoutEffect(() => {
    const scrollDelayMs = 100
    setTimeout(() => {
      if (scrollToHighlightedItemRef.current) {
        scrollToHighlightedItemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }, scrollDelayMs)
  }, [highlightedId])

  // Scroll down to selected item
  const handleSelectedClick = () => {
    if (scrollToSelectedItemRef.current) {
      scrollToSelectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  return (
    <MenuContentRoot>
      <ViewAllOrLessOptionsButton parentSize={parentSize} />
      <Header>{t2(TextId.HEADER_GV_PRODUCT_TYPE)}</Header>

      {menuLevel2 && menuLevel2[0] && (
        <DropdownWrapper>
          <TdsDropdown
            size="sm"
            open-direction="auto"
            name="dropdown"
            defaultValue={menuLevel2[0].id || undefined}
            onTdsChange={handleSubMenuDropdownSelection}
            selected-option={selectedMenuLevel2}
            key={selectedMenuLevel2}
            data-test-element-type={TestElementTypeId.AllViewDropDownLevel2}
          >
            {menuLevel2.map((menuItem) => (
              <TdsDropdownOption
                key={menuItem.id}
                value={menuItem.id}
                data-test-etel-variant-id={menuItem.id}
              >
                {menuItem.shortText}
              </TdsDropdownOption>
            ))}
          </TdsDropdown>
        </DropdownWrapper>
      )}
      <Header>{t2(TextId.HEADER_GV_PRODUCT_OPTIONS)}</Header>
      {menuLevel3 && (
        <DropdownWrapper>
          <TdsDropdown
            size="sm"
            open-direction="auto"
            name="dropdown"
            defaultValue={selectedMenuLevel3 || undefined}
            onTdsChange={handleSubMenuDropdownSelection}
            selected-option={selectedMenuLevel3}
            key={selectedMenuLevel3}
            data-test-element-type={TestElementTypeId.AllViewDropDownLevel3}
          >
            {menuLevel3.map((menuItem) => (
              <TdsDropdownOption
                key={menuItem.id}
                value={menuItem.id}
                data-test-etel-variant-id={menuItem.id}
              >
                {menuItem.shortText}
              </TdsDropdownOption>
            ))}
          </TdsDropdown>
        </DropdownWrapper>
      )}

      <SelectedOptionWrapper onClick={handleSelectedClick}>
        <div>{t2(TextId.GV_SELECTED)}&nbsp;</div>
        <SelectedOptionText>
          {capitalizeFirstLetter(
            menuLevel3 && menuLevel3[0].shortText?.includes('Cab colour')
              ? selectedCabColourNode?.shortText
              : menuLevel3 &&
                menuLevel3[0].shortText?.includes('Chassis colour')
              ? selectedChassiColourNode?.shortText
              : selectedLeafItem?.shortText,
          )}
        </SelectedOptionText>
      </SelectedOptionWrapper>

      <CardsWrapper>
        {menuLevel4 &&
          menuLevel4.map((leafItem, i) => {
            return (
              <OptionCard
                $isSelected={leafItem.isSelected}
                $isHighlighted={leafItem.id === highlightedId}
                key={leafItem.id + userSelectionTimestamp?.toString()}
                onClick={
                  leafItem.isSelected
                    ? () => {}
                    : () =>
                        handleItemSelection(
                          leafItem.id,
                          leafItem.shortText || '',
                        )
                }
                ref={
                  leafItem.id === highlightedId
                    ? scrollToHighlightedItemRef
                    : leafItem.isSelected
                    ? scrollToSelectedItemRef
                    : null
                }
                id={'nonColorCardLeaf' + i}
                data-test-element-type={TestElementTypeId.AllViewNonColourCard}
              >
                <CardTextWrapper>
                  <CardTextHeader>
                    {leafItem.shortText
                      ? capitalizeFirstLetter(leafItem.shortText)
                      : leafItem.id}
                  </CardTextHeader>

                  <ReadMoreAndSuperWrapper>
                    {leafItem.hasreadMore ? (
                      <CardReadMore
                        onClick={(e) => handleReadmoreClick(e, leafItem)}
                      >
                        <SvgInfo />
                        {t2(TextId.DESCRIPTION_VIEW_INFORMATION)}
                      </CardReadMore>
                    ) : (
                      <CardReadMore />
                    )}
                    {superEngines &&
                      superEngines.some((i) => i.id === leafItem.id) && (
                        <CardSuper>
                          <SvgScaniaSuper />
                        </CardSuper>
                      )}
                  </ReadMoreAndSuperWrapper>
                </CardTextWrapper>
                {leafItem.iconUrl && (
                  <CardImage>
                    <img src={leafItem.iconUrl} alt="truck" />
                  </CardImage>
                )}
                <CardStatusWrapper>
                  {leafItem.isSelected && <SvgTick />}
                  {currentlyLoadingId === leafItem.id && (
                    <TdsSpinner size="sm" />
                  )}
                </CardStatusWrapper>
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="top"
                    selector={'#nonColorCardLeaf' + i}
                    text={leafItem.id}
                  />
                )}
              </OptionCard>
            )
          })}
      </CardsWrapper>
    </MenuContentRoot>
  )
}
